import Text from '@creator/ui/components/Text/Text';
import { cn } from '@creator/ui/utils/ui';
import { useStoreState } from '@src/model/hooks';
import { usePost } from '@src/store/posts/hooks/usePost';
import { propsEqualityFn, storeEqualityFn } from '@src/utils/object-utils/object-utils';
import type { FC } from 'react';
import { memo } from 'react';
import PostMenuPopover from '../PostMenuPopover/PostMenuPopover';
import PostBasicInfo, { type PostBasicInfoProps } from '../Upvote/PostBasicInfo/PostBasicInfo';

export interface PostCardHeaderProps {
    className?: string;
    postId: string;
    tokenName: string;
    showPostTitle?: boolean;
}

const PostCardHeader: FC<PostCardHeaderProps> = props => {
    const { tokenName, postId, className } = props;

    const post = usePost(postId);
    const token = useStoreState(state => state.token.getToken(tokenName), storeEqualityFn);

    const renderPostBasicInfo = () => {
        if (!post) return null;

        const infoProps: PostBasicInfoProps = {
            className: 'text-sm',
            publisherName: post.publisherName,
            publisherProfileName: post.createdByWhitelistedUser ? post.publisherProfileName : '',
            publisherSlugUrl: post.publisherSlugUrl,
            createdAt: post.createdAt,
            updatedAt: post.updatedAt,
            tokenName: post.tokenName,
            showPartnerLabel: post.createdByWhitelistedUser,
            communityName: token?.title,
        };

        return <PostBasicInfo {...infoProps} />;
    };

    function renderTitle() {
        const { showPostTitle } = props;
        if (!showPostTitle || !post) return null;
        return (
            <div className="flex items-center justify-between">
                <Text enableMultiline={true} as="h1" className="text-base font-bold lg:text-lg">
                    {post?.title}
                </Text>
                <PostMenuPopover postId={postId} className="" tokenName={tokenName} />
            </div>
        );
    }

    function renderExcerpt() {
        if (!post?.excerpt || post.kind !== 'story') return null;
        return (
            <Text data-ssr="post-excerpt" enableMultiline={true} as="h2" className="text-base font-medium text-gray-500">
                {post.excerpt}
            </Text>
        );
    }

    return (
        <div className={cn('relative space-y-3', className)}>
            {renderTitle()}
            {renderPostBasicInfo()}
            {renderExcerpt()}
        </div>
    );
};

export default memo(PostCardHeader, propsEqualityFn);
